import * as React from 'react';
import {
    Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, IconButton, MenuItem, Paper, Stack, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { FgsManagement } from '../../controllers';
import { TokenValidator } from '../../utils';

const emptyForm = {client: '', carbonType: 'hollow', enabled: true, reason: ''};
const correlationId = () => window.crypto && window.crypto.randomUUID
    ? window.crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0;
        return (c === 'x' ? r : ((r & 3) | 8)).toString(16);
    });
const displayTime = value => value ? new Date(value).toLocaleString() : '—';

export default function PrintRouting({newClientMode = false}) {
    const [data, setData] = React.useState({routes: [], clients: []});
    const [loading, setLoading] = React.useState(true);
    const [busy, setBusy] = React.useState(false);
    const [message, setMessage] = React.useState(null);
    const [editor, setEditor] = React.useState(newClientMode ? {mode: 'create'} : null);
    const [form, setForm] = React.useState({...emptyForm});
    const [history, setHistory] = React.useState(null);
    const [statusDialog, setStatusDialog] = React.useState(null);

    const token = () => TokenValidator.GetInternalUser();
    const load = React.useCallback(async () => {
        setLoading(true);
        const response = await FgsManagement.ListPrintRoutes(await token());
        setLoading(false);
        if (!response || !response.success) {
            setMessage({severity: 'error', text: response ? response.message : 'Unable to load print routes'});
            return;
        }
        setData(response.result);
    }, []);
    React.useEffect(() => { load(); }, [load]);
    React.useEffect(() => {
        if (newClientMode) { setForm({...emptyForm}); setEditor({mode: 'create'}); }
    }, [newClientMode]);

    const openCreate = () => { setForm({...emptyForm}); setEditor({mode: 'create'}); };
    const openEdit = row => {
        setForm({client: row.client, carbonType: row.carbonType, enabled: row.enabled, reason: ''});
        setEditor({mode: 'edit', row});
    };
    const save = async () => {
        if (!form.client.trim()) return setMessage({severity: 'error', text: 'Client is required.'});
        if (form.reason.trim().length < 3) return setMessage({severity: 'error', text: 'A reason of at least 3 characters is required.'});
        setBusy(true);
        const payload = {carbonType: form.carbonType, enabled: form.enabled, reason: form.reason, correlationId: correlationId()};
        const response = editor.mode === 'create'
            ? await FgsManagement.CreatePrintRoute(await token(), {...payload, client: form.client})
            : await FgsManagement.UpdatePrintRoute(await token(), editor.row.id, payload);
        setBusy(false);
        if (!response || !response.success) {
            setMessage({severity: 'error', text: response ? response.message : 'Unable to save print route'});
            return;
        }
        setEditor(null);
        setMessage({severity: 'success', text: 'Carbon print route saved.'});
        await load();
    };
    const toggle = async () => {
        const {row, reason} = statusDialog;
        if (reason.trim().length < 3) return setMessage({severity: 'error', text: 'A reason of at least 3 characters is required.'});
        setBusy(true);
        const response = await FgsManagement.UpdatePrintRoute(await token(), row.id, {
            enabled: !row.enabled, reason, correlationId: correlationId()
        });
        setBusy(false);
        if (!response || !response.success) return setMessage({severity: 'error', text: response ? response.message : 'Unable to update route'});
        setStatusDialog(null);
        setMessage({severity: 'success', text: 'Print route status updated.'});
        await load();
    };
    const showHistory = async row => {
        setBusy(true);
        const response = await FgsManagement.PrintRouteHistory(await token(), row.id);
        setBusy(false);
        if (!response || !response.success) return setMessage({severity: 'error', text: response ? response.message : 'Unable to load history'});
        setHistory({row, entries: response.result});
    };

    return <Box>
        {message && <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{mb: 2}}>{message.text}</Alert>}
        <Alert severity='info' sx={{mb: 2}}>
            This view includes every active FGS client. Explicit routes are editable; legacy Carbon routes are read-only and choose Hollow/Solid automatically. Unconfigured means no confirmed production route. Primslab remains retired.
        </Alert>
        {!newClientMode && <Stack direction='row' justifyContent='flex-end' gap={1} sx={{mb: 2}}>
            <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading || busy}>Refresh</Button>
            <Button variant='contained' startIcon={<AddIcon />} onClick={openCreate} disabled={busy}>Add client route</Button>
        </Stack>}
        {newClientMode && <Paper variant='outlined' sx={{p: 2, mb: 2}}>
            <Typography variant='h6'>New client — production route</Typography>
            <Typography color='text.secondary'>Create the Carbon route here, then add one FGS assignment for each product in the FGS Assignments tab.</Typography>
        </Paper>}
        {!newClientMode && <TableContainer component={Paper}><Table size='small'>
            <TableHead><TableRow><TableCell>Client</TableCell><TableCell>Products</TableCell><TableCell>Destination</TableCell><TableCell>Type</TableCell><TableCell>Configuration</TableCell><TableCell>Status</TableCell><TableCell>Last modified</TableCell><TableCell align='right'>Actions</TableCell></TableRow></TableHead>
            <TableBody>
                {loading && <TableRow><TableCell colSpan={8} align='center'><CircularProgress size={28} /></TableCell></TableRow>}
                {!loading && data.routes.map(row => <TableRow key={row.key || row.id} hover>
                    <TableCell>{row.client}</TableCell>
                    <TableCell>{row.products && row.products.length ? row.products.join(', ') : '—'}</TableCell>
                    <TableCell>{row.destination === 'carbon' ? 'Carbon' : 'Unconfigured'}</TableCell>
                    <TableCell sx={{textTransform: 'capitalize'}}>{row.carbonType === 'automatic' ? 'Automatic (Hollow / Solid)' : (row.carbonType || '—')}</TableCell>
                    <TableCell>{row.routeSource === 'explicit' ? 'Explicit route' : row.routeSource === 'legacy' ? 'Legacy automatic' : 'No route'}</TableCell>
                    <TableCell><Chip size='small' color={row.enabled ? 'success' : row.routeSource === 'unconfigured' ? 'warning' : 'default'} label={row.enabled ? 'Active' : row.routeSource === 'unconfigured' ? 'Needs route' : 'Inactive'} /></TableCell>
                    <TableCell>{displayTime(row.updatedAt)}</TableCell><TableCell align='right'>
                        {row.editable && <>
                            <Tooltip title='Edit'><IconButton onClick={() => openEdit(row)} disabled={busy}><EditIcon /></IconButton></Tooltip>
                            <Tooltip title='History'><IconButton onClick={() => showHistory(row)} disabled={busy}><HistoryIcon /></IconButton></Tooltip>
                            <Tooltip title={row.enabled ? 'Deactivate' : 'Activate'}><IconButton onClick={() => setStatusDialog({row, reason: ''})} disabled={busy}><PowerSettingsNewIcon /></IconButton></Tooltip>
                        </>}
                    </TableCell>
                </TableRow>)}
                {!loading && !data.routes.length && <TableRow><TableCell colSpan={8} align='center'>No FGS clients or print routes found.</TableCell></TableRow>}
            </TableBody>
        </Table></TableContainer>}

        <Dialog open={editor !== null} onClose={() => !busy && !newClientMode && setEditor(null)} maxWidth='sm' fullWidth>
            <DialogTitle>{editor && editor.mode === 'edit' ? 'Edit Carbon route' : 'Add Carbon route'}</DialogTitle>
            <DialogContent><Stack gap={2} sx={{pt: 1}}>
                <Autocomplete freeSolo options={data.clients} value={form.client || null} inputValue={form.client}
                    disabled={editor && editor.mode === 'edit'}
                    onChange={(_, value) => setForm({...form, client: value || ''})}
                    onInputChange={(_, value) => setForm({...form, client: value})}
                    renderInput={params => <TextField {...params} label='Client' inputProps={{...params.inputProps, maxLength: 100}}
                        helperText="Select an observed client or type the exact case_client for a new client." />} />
                <TextField label='Destination' value='Carbon' InputProps={{readOnly: true}} />
                <TextField select label='Carbon type' value={form.carbonType} onChange={e => setForm({...form, carbonType: e.target.value})}>
                    <MenuItem value='hollow'>Hollow</MenuItem><MenuItem value='solid'>Solid</MenuItem>
                </TextField>
                <TextField required multiline minRows={2} label='Reason' value={form.reason} inputProps={{maxLength: 500}}
                    onChange={e => setForm({...form, reason: e.target.value})} />
                <Alert severity='warning'>An active route can release matching orders waiting at G-Code Complete on the next Controller cycle.</Alert>
            </Stack></DialogContent>
            <DialogActions>{!newClientMode && <Button onClick={() => setEditor(null)} disabled={busy}>Cancel</Button>}<Button variant='contained' onClick={save} disabled={busy}>Save route</Button></DialogActions>
        </Dialog>

        <Dialog open={history !== null} onClose={() => setHistory(null)} maxWidth='md' fullWidth>
            <DialogTitle>Print route history</DialogTitle><DialogContent>
                {history && history.entries.map(entry => <Paper key={entry.correlation_id} variant='outlined' sx={{p: 2, mb: 1}}>
                    <Typography><strong>{entry.action}</strong> — {entry.reason}</Typography>
                    <Typography color='text.secondary'>{displayTime(entry.created_at)} / {entry.actor || 'Deleted user'}</Typography>
                </Paper>)}
                {history && !history.entries.length && <Alert severity='info'>No history found.</Alert>}
            </DialogContent><DialogActions><Button onClick={() => setHistory(null)}>Close</Button></DialogActions>
        </Dialog>
        <Dialog open={statusDialog !== null} onClose={() => !busy && setStatusDialog(null)} maxWidth='sm' fullWidth>
            <DialogTitle>{statusDialog && statusDialog.row.enabled ? 'Deactivate Carbon route' : 'Activate Carbon route'}</DialogTitle>
            <DialogContent><Stack gap={2} sx={{pt: 1}}>
                {statusDialog && <Typography>{statusDialog.row.client} — Carbon {statusDialog.row.carbonType}</Typography>}
                <Alert severity='warning'>Deactivating prevents future automatic Carbon routing. Existing production history is preserved.</Alert>
                <TextField required multiline minRows={2} label='Reason' inputProps={{maxLength: 500}}
                    value={statusDialog ? statusDialog.reason : ''}
                    onChange={e => setStatusDialog({...statusDialog, reason: e.target.value})} />
            </Stack></DialogContent>
            <DialogActions><Button onClick={() => setStatusDialog(null)} disabled={busy}>Cancel</Button><Button variant='contained' onClick={toggle} disabled={busy}>Confirm</Button></DialogActions>
        </Dialog>
    </Box>;
}
